// src/pages/comercial/AnaliseVendas.js
import React, { useEffect, useMemo, useState } from "react";

// API e contexto
import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Charts (igual Dashboard / MenuSearchMateriais)
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

// MUI
import {
  Card,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Layout JFB
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// PageHeader (título/subtítulo vêm de routes.js)
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import AnaliseVendasMaterialDetalheDialog from "pages/comercial/components/AnaliseVendasMaterialDetalheDialog";
import AnaliseVendasMateriaisTable from "pages/comercial/components/AnaliseVendasMateriaisTable";
import AnaliseVendasDetalheTable from "pages/comercial/components/AnaliseVendasDetalheTable";

const MESES = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const MESES_LABELS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const DIAS = Array.from({ length: 31 }, (_, i) => i + 1);

const formatDateInput = (date) => {
  const ano = date.getFullYear();
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const dia = String(date.getDate()).padStart(2, "0");

  return `${ano}-${mes}-${dia}`;
};

const parseDateInput = (value) => {
  const partes = String(value || "")
    .split("-")
    .map(Number);

  if (
    partes.length !== 3 ||
    !Number.isFinite(partes[0]) ||
    !Number.isFinite(partes[1]) ||
    !Number.isFinite(partes[2])
  ) {
    return null;
  }

  const [ano, mes, dia] = partes;
  const data = new Date(ano, mes - 1, dia);

  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
    return null;
  }

  return data;
};

const fmt = new Intl.NumberFormat("pt-PT", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const METRIC_CONFIG = {
  eur: {
    title: "Vendas mensais (sem IVA, sem vasilhame)",
    caption: "Totais anuais indicados na legenda. Valores em euros.",
    suffix: " €",
  },
  litros: {
    title: "Litros faturados mensalmente",
    caption: "Totais anuais indicados na legenda. Litros.",
    suffix: "",
  },
  pesoBru: {
    title: "Peso bruto mensal faturado",
    caption: "Totais anuais indicados na legenda. Peso bruto.",
    suffix: "",
  },
  qtd: {
    title: "Quantidades faturadas mensalmente",
    caption: "Totais anuais indicados na legenda. Quantidades (qtd).",
    suffix: "",
  },
  clientes: {
    title: "Clientes distintos (mensal)",
    caption: "Número de clientes diferentes com vendas no período.",
    suffix: "",
  },
};

const MemoizedDefaultLineChart = React.memo(DefaultLineChart);

function AnaliseVendas() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();
  const theme = useTheme();

  const isAdminLike = useMemo(
    () => temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria"),
    [temFuncao]
  );
  const isComercial = useMemo(() => temFuncao("Comercial"), [temFuncao]);
  const refUser = useMemo(
    () => (userData?.refvendedor ? String(userData.refvendedor) : ""),
    [userData]
  );

  // quando Comercial e não administrador, este vendedor é obrigatório
  const vendedorForcado = !isAdminLike && isComercial && refUser ? refUser : "";

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat("pt-PT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const colorMap = {
    warning: theme.palette.warning.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    info: theme.palette.info.main, // ✅ para a linha Compras
  };

  const [loading, setLoading] = useState(false);

  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [tabResultados, setTabResultados] = useState(0);

  const getPeriodoBody = ({ modo, dataIni, dataFim, anos, mesI, mesF, diaI, diaF }) => {
    if (modo === "entreDatas") {
      return {
        modoPeriodo: "entreDatas",
        dataInicial: dataIni || "",
        dataFinal: dataFim || "",
        anos: [],
      };
    }

    return {
      modoPeriodo: "comparativo",
      dataInicial: null,
      dataFinal: null,
      anos: Array.isArray(anos) ? anos.map(Number).filter(Number.isFinite) : [],
      mesInicial: Number(mesI),
      mesFinal: Number(mesF),
      diaInicial: Number(diaI),
      diaFinal: Number(diaF),
    };
  };

  const getPeriodoKey = ({ modo, dataIni, dataFim, anos, mesI, mesF, diaI, diaF }) => {
    const body = getPeriodoBody({
      modo,
      dataIni,
      dataFim,
      anos,
      mesI,
      mesF,
      diaI,
      diaF,
    });

    if (Array.isArray(body.anos)) {
      body.anos = [...body.anos].sort((a, b) => a - b);
    }

    return JSON.stringify(body);
  };

  // ----------------- Filtros -----------------
  const hoje = useMemo(() => new Date(), []);

  const anoAtual = hoje.getFullYear();

  const [modoPeriodo, setModoPeriodo] = useState("entreDatas");

  const [dataInicial, setDataInicial] = useState(() => formatDateInput(new Date(anoAtual, 0, 1)));

  const [dataFinal, setDataFinal] = useState(() => formatDateInput(hoje));

  const anosOptions = useMemo(() => {
    const anos = [];
    for (let a = anoAtual; a >= 2023; a--) anos.push(a);
    return anos;
  }, [anoAtual]);

  const [anosSel, setAnosSel] = useState([anoAtual, anoAtual - 1, anoAtual - 2]);

  //const mesAtual = new Date().getMonth() + 1;

  const [mesIni, setMesIni] = useState(1);
  const [mesFim, setMesFim] = useState(hoje.getMonth() + 1);

  const [diaIni, setDiaIni] = useState(1);
  const [diaFim, setDiaFim] = useState(hoje.getDate());

  const [vendedores, setVendedores] = useState([]);
  // Guarda apenas os refs selecionados
  const [vendedoresSel, setVendedoresSel] = useState([]);

  const [fabricantes, setFabricantes] = useState([]);
  const [fabricanteSel, setFabricanteSel] = useState(null);

  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);

  const [familias, setFamilias] = useState([]);
  const [familiasSel, setFamiliasSel] = useState([]); // Guarda apenas os refs selecionados

  const [materiais, setMateriais] = useState([]);
  const [materiaisSel, setMateriaisSel] = useState([]); // Guarda apenas os refs selecionados
  const [materiaisDisabled, setMateriaisDisabled] = useState(true); // controla se o accordion deve estar desativado (por regra de famílias)
  const [materiaisLoadedKey, setMateriaisLoadedKey] = useState(""); // evita refazer pedidos iguais
  const [materiaisLoading, setMateriaisLoading] = useState(false);
  const [materiaisFiltro, setMateriaisFiltro] = useState("");

  const [embalagens, setEmbalagens] = useState([]);
  const [embalagensSel, setEmbalagensSel] = useState([]);

  const [nomeFiltroModo, setNomeFiltroModo] = useState("contém"); // "contém" | "naoContém"
  const [nomeFiltroTexto, setNomeFiltroTexto] = useState("");

  const [imgsByRef, setImgsByRef] = useState({});

  // Apenas um accordion aberto de cada vez
  const [expandedPanel, setExpandedPanel] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  // ----------------- Dados -----------------
  const [resumoMensal, setResumoMensal] = useState([]); // para gráfico
  const [resumoMensalCompras, setResumoMensalCompras] = useState([]); // para gráfico (compras)

  const [rows, setRows] = useState([]); // para tabela

  const [materiaisAgrupados, setMateriaisAgrupados] = useState([]);
  const [resumoMateriais, setResumoMateriais] = useState(null);
  const [materiaisAgrupadosLoading, setMateriaisAgrupadosLoading] = useState(false);
  const [materiaisAgrupadosCarregados, setMateriaisAgrupadosCarregados] = useState(false);

  const [dialogMaterialOpen, setDialogMaterialOpen] = useState(false);
  const [materialDetalheSelecionado, setMaterialDetalheSelecionado] = useState(null);
  const [linhasDetalheMaterial, setLinhasDetalheMaterial] = useState([]);
  const [detalheMaterialLoading, setDetalheMaterialLoading] = useState(false);

  const [totalLinhas, setTotalLinhas] = useState(0);
  const [limiteLinhas, setLimiteLinhas] = useState(100);

  const limiteLinhasOpcoes = useMemo(() => {
    const total = Number(totalLinhas || 0);

    // Antes da primeira análise, mantém apenas o valor predefinido.
    if (total <= 0) {
      return [{ value: 100, label: "100" }];
    }

    const opcoes = [];

    if (total > 100) {
      opcoes.push({ value: 100, label: "100" });
    }

    if (total > 1000) {
      opcoes.push({ value: 1000, label: "1 000" });
    }

    if (total > 5000) {
      opcoes.push({ value: 5000, label: "5 000" });
    }

    opcoes.push({
      value: 0,
      label: `Todas (${total.toLocaleString("pt-PT")})`,
      disabled: total > 5000,
    });

    return opcoes;
  }, [totalLinhas]);

  useEffect(() => {
    const total = Number(totalLinhas || 0);

    if (total <= 0) return;

    const valoresValidos = limiteLinhasOpcoes
      .filter((opcao) => !opcao.disabled)
      .map((opcao) => Number(opcao.value));

    if (!valoresValidos.includes(Number(limiteLinhas))) {
      setLimiteLinhas(valoresValidos[0] ?? 100);
    }
  }, [totalLinhas, limiteLinhas, limiteLinhasOpcoes]);

  const [clientesPorVendedor, setClientesPorVendedor] = useState([]); // só para métrica "clientes"

  const [metric, setMetric] = useState("eur");

  const [periodoKeyCombos, setPeriodoKeyCombos] = useState("");

  const limparResultadosAnalise = () => {
    setMostrarResultados(false);
    setResumoMensal([]);
    setResumoMensalCompras([]);
    setRows([]);
    setTotalLinhas(0);
    setClientesPorVendedor([]);

    setTabResultados(0);
    setMateriaisAgrupados([]);
    setResumoMateriais(null);
    setMateriaisAgrupadosCarregados(false);

    setDialogMaterialOpen(false);
    setMaterialDetalheSelecionado(null);
    setLinhasDetalheMaterial([]);
  };

  const validarPeriodoAtual = () => {
    if (modoPeriodo === "entreDatas") {
      const inicio = parseDateInput(dataInicial);
      const fim = parseDateInput(dataFinal);

      if (!inicio || !fim) {
        showAlert("Indica a data inicial e a data final.", "warning");
        return false;
      }

      if (fim < inicio) {
        showAlert("A data final não pode ser anterior à data inicial.", "warning");
        return false;
      }

      return true;
    }

    if (!Array.isArray(anosSel) || anosSel.length === 0) {
      showAlert("Seleciona pelo menos um ano.", "warning");
      return false;
    }

    if (Number(mesFim) < Number(mesIni)) {
      showAlert("O mês final não pode ser anterior ao mês inicial.", "warning");
      return false;
    }

    if (Number(mesIni) === Number(mesFim) && Number(diaFim) < Number(diaIni)) {
      showAlert("O dia final não pode ser anterior ao dia inicial.", "warning");
      return false;
    }

    return true;
  };

  const bodyPeriodoAtual = useMemo(
    () =>
      getPeriodoBody({
        modo: modoPeriodo,
        dataIni: dataInicial,
        dataFim: dataFinal,
        anos: anosSel,
        mesI: mesIni,
        mesF: mesFim,
        diaI: diaIni,
        diaF: diaFim,
      }),
    [modoPeriodo, dataInicial, dataFinal, anosSel, mesIni, mesFim, diaIni, diaFim]
  );

  // ============================================================
  // Combos iniciais
  // ============================================================
  useEffect(() => {
    const carregarCombos = async () => {
      setLoading(true);
      try {
        const bodyPeriodo = bodyPeriodoAtual;

        const bodyClientes = {
          refvendedor: vendedorForcado || "",
          funcoes: userData?.funcoes || [],
          ...bodyPeriodo,
        };

        const [
          [dadosVendedores, errVendedores],
          [dadosFabricantes, errFabricantes],
          [dadosClientes, errClientes],
        ] = await Promise.all([
          handleApi(api.post("Comercial/SelVendedoresCombo", bodyPeriodo)),
          handleApi(api.post("Comercial/SelFabricantesCombo", {})),
          handleApi(api.post("Comercial/SelClientesCombo", bodyClientes)),
        ]);

        // Vendedores
        if (errVendedores) {
          showAlert(errVendedores, "error");
        } else {
          const lista = Array.isArray(dadosVendedores) ? dadosVendedores : [];

          if (vendedorForcado) {
            // Comercial: só vê o próprio vendedor, já selecionado
            const apenasMeu = lista.filter((v) => String(v.ref) === vendedorForcado);
            setVendedores(apenasMeu);

            setVendedoresSel(apenasMeu.map((v) => v.ref));
          } else {
            // restantes funções: todos selecionados por defeito
            setVendedores(lista);

            setVendedoresSel(lista.map((v) => v.ref));
          }
        }

        // Fabricantes
        if (errFabricantes) {
          showAlert(errFabricantes, "error");
        } else {
          const lista = Array.isArray(dadosFabricantes) ? dadosFabricantes : [];
          const todos = { ref: "", nome: "TODOS" };
          const final = [todos, ...lista];

          setFabricantes(final);

          setFabricanteSel((prev) => {
            const refPrev = String(prev?.ref || "").trim();

            if (!refPrev) return todos;

            return final.find((x) => String(x?.ref || "").trim() === refPrev) || todos;
          });
        }

        // Clientes
        if (errClientes) {
          showAlert(errClientes, "error");
        } else {
          const lista = Array.isArray(dadosClientes) ? dadosClientes : [];
          const todos = { ref: "", nome: "TODOS" };
          const final = [todos, ...lista];

          setClientes(final);

          setClienteSel((prev) => {
            const refPrev = String(prev?.ref || "").trim();

            if (!refPrev) return todos;

            return final.find((x) => String(x?.ref || "").trim() === refPrev) || todos;
          });
        }

        // Famílias
        {
          const [dados, err] = await handleApi(api.post("Comercial/SelFamiliasCombo", {}));
          if (err) {
            showAlert(err, "error");
          } else {
            const lista = Array.isArray(dados) ? dados : [];
            setFamilias(lista);

            // guardar APENAS as refs das famílias selecionadas
            setFamiliasSel(lista.map((f) => f.ref));
          }
        }

        // Embalagens
        {
          const [dados, err] = await handleApi(api.post("Comercial/SelEmbalagensCombo", {}));
          if (err) {
            showAlert(err, "error");
          } else {
            const lista = Array.isArray(dados) ? dados : [];
            setEmbalagens(lista);
            setEmbalagensSel(lista.map((e) => e.ref));
          }
        }
      } catch {
        showAlert("Erro ao carregar dados auxiliares.", "error");
      } finally {
        setPeriodoKeyCombos(
          getPeriodoKey({
            modo: modoPeriodo,
            dataIni: dataInicial,
            dataFim: dataFinal,
            anos: anosSel,
            mesI: mesIni,
            mesF: mesFim,
            diaI: diaIni,
            diaF: diaFim,
          })
        );
        setLoading(false);
      }
    };

    carregarCombos();
  }, [
    showAlert,
    vendedorForcado,
    bodyPeriodoAtual,
    modoPeriodo,
    dataInicial,
    dataFinal,
    anosSel,
    mesIni,
    mesFim,
    diaIni,
    diaFim,
    userData,
  ]);

  const carregarImagensMateriais = async (refs) => {
    const lista = Array.from(
      new Set((refs || []).map((r) => String(r || "").trim()).filter(Boolean))
    );

    // pedir apenas as que ainda não temos
    const faltam = lista.filter((r) => !(imgsByRef || {})[r]);

    if (faltam.length === 0) return;

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMateriaisImagens", { refs: faltam })
    );

    if (err) return;

    const mapa = {};
    (Array.isArray(dados) ? dados : []).forEach((x) => {
      const ref = String(x?.refmaterial || "").trim();
      const url = String(x?.url || "").trim();
      if (ref && url) mapa[ref] = url;
    });

    setImgsByRef((prev) => ({ ...(prev || {}), ...mapa }));
  };

  // ============================================================
  // Carregar análise de vendas
  // ============================================================
  const carregarDados = async (override = {}) => {
    setLoading(true);

    try {
      const vendSel = override.vendedoresSel ?? vendedoresSel;
      const famSel = override.familiasSel ?? familiasSel;
      const matSel = override.materiaisSel ?? materiaisSel;
      const embSel = override.embalagensSel ?? embalagensSel;

      // se houver vendedor forçado (Comercial), ignora seleção manual
      let vendedoresApi;
      if (vendedorForcado) {
        vendedoresApi = [vendedorForcado];
      } else {
        const isTodosVend = vendSel.length === 0 || vendSel.length === vendedores.length;
        vendedoresApi = isTodosVend ? [] : vendSel;
      }

      const isTodasFam = famSel.length === 0 || famSel.length === familias.length;

      const isTodosMat =
        materiaisDisabled || matSel.length === 0 || matSel.length === materiais.length;

      const isTodasEmb = embSel.length === 0 || embSel.length === embalagens.length;

      const body = {
        ...bodyPeriodoAtual,
        vendedores: vendedoresApi,
        refFabricante: fabricanteSel?.ref || "",
        refCliente: clienteSel?.ref || "",
        metric: metric,
        familias: isTodasFam ? [] : famSel,
        materiais: isTodosMat ? [] : matSel,
        embalagens: isTodasEmb ? [] : embSel,
        // backend vai aplicar sempre este filtro se não for string vazia
        refvendedor: vendedorForcado || "",
        // filtro de texto no nome do material
        textoMaterial: nomeFiltroTexto.trim(),
        nomeNaoContem: nomeFiltroModo === "naoContem",
        funcoes: userData?.funcoes || [],
        limiteLinhas,
      };

      const [dados, err] = await handleApi(api.post("Comercial/SelAnaliseVendas", body));

      console.log("dados", dados);
      console.log("err", err);

      if (err) {
        // erro lógico da API (success = false, etc.)
        showAlert(err, "error");
        setResumoMensal([]);
        setResumoMensalCompras([]);

        setRows([]);
        setTotalLinhas(0);
        setLoading(false); // defesa extra para garantir que o spinner pára
        return;
      }

      const resumo = Array.isArray(dados?.resumo) ? dados.resumo : [];
      const resumoCompras = Array.isArray(dados?.resumoCompras) ? dados.resumoCompras : [];

      const linhas = Array.isArray(dados?.linhas) ? dados.linhas : [];
      const totalLinhasApi = Number(dados?.totalLinhas ?? linhas.length ?? 0);

      const clientesVend = Array.isArray(dados?.clientesPorVendedor)
        ? dados.clientesPorVendedor
        : [];

      // console.log("linhas", linhas);

      setResumoMensal(resumo);
      setResumoMensalCompras(resumoCompras);

      setRows(linhas);
      setTotalLinhas(totalLinhasApi);
      setClientesPorVendedor(metric === "clientes" ? clientesVend : []);

      carregarImagensMateriais(linhas.map((l) => l.refmaterial));

      // aviso explícito quando não há dados
      if (resumo.length === 0 && linhas.length === 0) {
        showAlert("Sem dados com os filtros selecionados.", "info");
      }
    } catch (e) {
      console.error(e);
      showAlert("Erro ao carregar análise de vendas.", "error");
      setResumoMensal([]);
      setResumoMensalCompras([]);

      setRows([]);
      setTotalLinhas(0);
      setClientesPorVendedor([]);
    } finally {
      // em condições normais já terá sido posto a false acima,
      // mas o finally garante que nunca fica preso em true
      setLoading(false);
    }
  };

  const carregarMateriaisAgrupados = async () => {
    if (materiaisAgrupadosLoading || materiaisAgrupadosCarregados) return;

    setMateriaisAgrupadosLoading(true);

    try {
      let vendedoresApi;

      if (vendedorForcado) {
        vendedoresApi = [vendedorForcado];
      } else {
        const isTodosVend =
          vendedoresSel.length === 0 || vendedoresSel.length === vendedores.length;

        vendedoresApi = isTodosVend ? [] : vendedoresSel;
      }

      const isTodasFam = familiasSel.length === 0 || familiasSel.length === familias.length;

      const isTodosMat =
        materiaisDisabled || materiaisSel.length === 0 || materiaisSel.length === materiais.length;

      const isTodasEmb = embalagensSel.length === 0 || embalagensSel.length === embalagens.length;

      const body = {
        ...bodyPeriodoAtual,
        vendedores: vendedoresApi,
        refFabricante: fabricanteSel?.ref || "",
        refCliente: clienteSel?.ref || "",
        metric,
        familias: isTodasFam ? [] : familiasSel,
        materiais: isTodosMat ? [] : materiaisSel,
        embalagens: isTodasEmb ? [] : embalagensSel,
        refvendedor: vendedorForcado || "",
        textoMaterial: nomeFiltroTexto.trim(),
        nomeNaoContem: nomeFiltroModo === "naoContem",
        funcoes: userData?.funcoes || [],
        modo: "materiais",
      };

      console.log("SelAnaliseVendas materiais - body", body);

      const [dados, err, meta] = await handleApi(api.post("Comercial/SelAnaliseVendas", body));

      console.log("SelAnaliseVendas materiais - dados", dados);
      console.log("SelAnaliseVendas materiais - err", err);
      console.log("SelAnaliseVendas materiais - meta", meta);

      if (err || meta?.success === false) {
        showAlert(
          meta?.message || err || "Erro ao carregar vendas agrupadas por material.",
          meta?.severity || "error"
        );
        setMateriaisAgrupados([]);
        setResumoMateriais(null);
        setMateriaisAgrupadosCarregados(false);
        return;
      }

      const lista = Array.isArray(dados?.materiais) ? dados.materiais : [];

      setMateriaisAgrupados(lista);
      setResumoMateriais(dados?.resumoMateriais || null);
      setMateriaisAgrupadosCarregados(true);

      carregarImagensMateriais(lista.map((linha) => linha.refmaterial));
    } catch (e) {
      console.error(e);
      showAlert("Erro ao carregar vendas agrupadas por material.", "error");
      setMateriaisAgrupados([]);
      setResumoMateriais(null);
      setMateriaisAgrupadosCarregados(false);
    } finally {
      setMateriaisAgrupadosLoading(false);
    }
  };

  const abrirDetalheMaterial = async (material) => {
    const refMaterial = String(material?.refmaterial || "").trim();

    if (!refMaterial) {
      showAlert("Referência de material inválida.", "error");
      return;
    }

    setMaterialDetalheSelecionado(material);
    setLinhasDetalheMaterial([]);
    setDialogMaterialOpen(true);
    setDetalheMaterialLoading(true);

    try {
      let vendedoresApi;

      if (vendedorForcado) {
        vendedoresApi = [vendedorForcado];
      } else {
        const isTodosVend =
          vendedoresSel.length === 0 || vendedoresSel.length === vendedores.length;

        vendedoresApi = isTodosVend ? [] : vendedoresSel;
      }

      const isTodasFam = familiasSel.length === 0 || familiasSel.length === familias.length;

      const isTodosMat =
        materiaisDisabled || materiaisSel.length === 0 || materiaisSel.length === materiais.length;

      const isTodasEmb = embalagensSel.length === 0 || embalagensSel.length === embalagens.length;

      const body = {
        ...bodyPeriodoAtual,
        vendedores: vendedoresApi,
        refFabricante: fabricanteSel?.ref || "",
        refCliente: clienteSel?.ref || "",
        metric,
        familias: isTodasFam ? [] : familiasSel,
        materiais: isTodosMat ? [] : materiaisSel,
        embalagens: isTodasEmb ? [] : embalagensSel,
        refvendedor: vendedorForcado || "",
        textoMaterial: nomeFiltroTexto.trim(),
        nomeNaoContem: nomeFiltroModo === "naoContem",
        funcoes: userData?.funcoes || [],
        modo: "detalheMaterial",
        refMaterialDetalhe: refMaterial,
      };

      const [dados, err, meta] = await handleApi(api.post("Comercial/SelAnaliseVendas", body));

      if (err || meta?.success === false) {
        showAlert(
          meta?.message || err || "Erro ao carregar o detalhe de faturação do material.",
          meta?.severity || "error"
        );

        setLinhasDetalheMaterial([]);
        return;
      }

      const lista = Array.isArray(dados?.linhas) ? dados.linhas : [];

      setLinhasDetalheMaterial(lista);

      carregarImagensMateriais(lista.map((linha) => linha.refmaterial));
    } catch (e) {
      console.error(e);
      showAlert("Erro ao carregar o detalhe de faturação do material.", "error");
      setLinhasDetalheMaterial([]);
    } finally {
      setDetalheMaterialLoading(false);
    }
  };

  const fecharDetalheMaterial = () => {
    setDialogMaterialOpen(false);
    setMaterialDetalheSelecionado(null);
    setLinhasDetalheMaterial([]);
  };

  // Sem carregamento automático da análise.
  // Os resultados só devem ser pedidos após clique no botão.

  const periodoKeyAtual = useMemo(
    () =>
      getPeriodoKey({
        modo: modoPeriodo,
        dataIni: dataInicial,
        dataFim: dataFinal,
        anos: anosSel,
        mesI: mesIni,
        mesF: mesFim,
        diaI: diaIni,
        diaF: diaFim,
      }),
    [modoPeriodo, dataInicial, dataFinal, anosSel, mesIni, mesFim, diaIni, diaFim]
  );

  const combosDependentesDesatualizadas =
    periodoKeyCombos !== "" && periodoKeyCombos !== periodoKeyAtual;

  // ============================================================
  // Preparar gráfico (igual ao padrão Dashboard / MenuSearchMateriais)
  // ============================================================
  const vendasChart = useMemo(() => {
    if (!Array.isArray(resumoMensal)) return null;

    const getValor = (r) => {
      if (metric === "eur") return Number(r.valortotal ?? r.valor ?? 0);
      if (metric === "litros") return Number(r.volume ?? 0);
      if (metric === "pesoBru") return Number(r.pesobruto ?? 0);
      if (metric === "qtd") return Number(r.quantidade ?? 0);
      if (metric === "clientes") return Number(r.clientes ?? 0);

      return 0;
    };

    if (modoPeriodo === "entreDatas") {
      const inicio = parseDateInput(dataInicial);
      const fim = parseDateInput(dataFinal);

      if (!inicio || !fim || fim < inicio) return null;

      const labels = [];
      const chavesMes = [];

      const cursor = new Date(inicio.getFullYear(), inicio.getMonth(), 1);
      const ultimoMes = new Date(fim.getFullYear(), fim.getMonth(), 1);

      while (cursor <= ultimoMes) {
        const ano = cursor.getFullYear();
        const mes = cursor.getMonth() + 1;

        chavesMes.push(`${ano}-${String(mes).padStart(2, "0")}`);
        labels.push(`${MESES_LABELS[mes - 1]} ${ano}`);

        cursor.setMonth(cursor.getMonth() + 1);
      }

      const indicePorMes = new Map(chavesMes.map((chave, index) => [chave, index]));

      const vendasPorMes = new Array(chavesMes.length).fill(0);
      let totalVendas = 0;

      for (const r of resumoMensal) {
        const ano = Number(r.ano);
        const mes = Number(r.mes);
        const chave = `${ano}-${String(mes).padStart(2, "0")}`;
        const index = indicePorMes.get(chave);

        if (index == null) continue;

        const valor = getValor(r);

        vendasPorMes[index] += valor;
        totalVendas += valor;
      }

      const datasets = [
        {
          label: "Vendas",
          data: vendasPorMes,
          tension: 0.4,
          pointRadius: 3,
          borderWidth: 3,
          color: "warning",
        },
      ];

      const totaisDataset = {
        Vendas: totalVendas,
      };

      const temFabricante = Boolean((fabricanteSel?.ref || "").trim());
      const temCompras = Array.isArray(resumoMensalCompras) && resumoMensalCompras.length > 0;

      if (isAdminLike && temFabricante && temCompras && metric !== "clientes") {
        const comprasPorMes = new Array(chavesMes.length).fill(0);
        let totalCompras = 0;

        for (const r of resumoMensalCompras) {
          const ano = Number(r.ano);
          const mes = Number(r.mes);
          const chave = `${ano}-${String(mes).padStart(2, "0")}`;
          const index = indicePorMes.get(chave);

          if (index == null) continue;

          let valor = 0;

          if (metric === "eur") {
            valor = Number(r.valortotal ?? r.valor ?? 0);
          } else if (metric === "litros") {
            valor = Number(r.volume ?? 0);
          } else if (metric === "qtd") {
            valor = Number(r.quantidade ?? 0);
          }

          comprasPorMes[index] += valor;
          totalCompras += valor;
        }

        datasets.push({
          label: "Compras",
          data: comprasPorMes,
          tension: 0.4,
          pointRadius: 3,
          borderWidth: 3,
          color: "info",
        });

        totaisDataset.Compras = totalCompras;
      }

      return {
        labels,
        datasets,
        totaisDataset,
        totaisAno: {},
        totaisComprasAno: {},
      };
    }

    // ------------------------------------------------------------
    // 1) Anos-base: vêm SEMPRE de anosSel (igual ao Dashboard)
    // ------------------------------------------------------------
    const anos =
      Array.isArray(anosSel) && anosSel.length > 0
        ? [...anosSel]
            .map(Number)
            .filter(Number.isFinite)
            .sort((a, b) => b - a)
        : [];

    if (anos.length === 0) return null;

    // ------------------------------------------------------------
    // 2) Intervalo de meses selecionado
    // ------------------------------------------------------------
    const mi = Number(mesIni); // 1..12
    const mf = Number(mesFim); // 1..12

    // ------------------------------------------------------------
    // 3) Inicialização
    //    - null fora do intervalo
    //    - 0 dentro do intervalo (para o ano aparecer mesmo sem vendas)
    // ------------------------------------------------------------
    const porAno = {};
    const totaisAno = {};

    anos.forEach((ano) => {
      porAno[ano] = new Array(12).fill(null);
      totaisAno[ano] = 0;

      for (let m = mi; m <= mf; m++) {
        porAno[ano][m - 1] = 0;
      }
    });

    // ------------------------------------------------------------
    // 4) Aplicar dados reais (se existirem)
    // ------------------------------------------------------------
    for (const r of resumoMensal) {
      const ano = Number(r.ano);
      const mes = Number(r.mes);

      if (!anos.includes(ano)) continue;
      if (mes < mi || mes > mf) continue;

      const valor = getValor(r);
      porAno[ano][mes - 1] += valor;
      totaisAno[ano] += valor;
    }

    // ------------------------------------------------------------
    // 5) Meses futuros do ano atual -> null (igual Dashboard)
    // ------------------------------------------------------------
    const anoAtualLocal = new Date().getFullYear();
    const mesAtualIdx = new Date().getMonth(); // 0..11

    if (porAno[anoAtualLocal]) {
      for (let m = mesAtualIdx + 1; m < 12; m++) {
        porAno[anoAtualLocal][m] = null;
      }
    }

    // ------------------------------------------------------------
    // 6) Labels (OBRIGATÓRIO para o DefaultLineChart)
    // ------------------------------------------------------------
    const labels = MESES_LABELS;

    // ------------------------------------------------------------
    // 7) Cores (iguais ao Dashboard)
    // ------------------------------------------------------------
    const anoAnterior = anoAtualLocal - 1;
    const anoAntepenultimo = anoAtualLocal - 2;

    const corPorAno = {
      [anoAtualLocal]: "warning",
      [anoAnterior]: "primary",
      [anoAntepenultimo]: "secondary",
    };

    const apenasUmAno = anos.length === 1;

    const datasetsBase = anos.map((ano) => ({
      label: apenasUmAno ? `${ano}_Vendas` : String(ano),
      data: porAno[ano],
      tension: 0.4,
      pointRadius: 3,
      borderWidth: 3,
      color: corPorAno[ano] || "info",
    }));

    const datasets = [...datasetsBase];

    // ------------------------------------------------------------
    // 8) Linha Compras (só 1 ano e só quando há fabricante selecionado)
    // ------------------------------------------------------------
    const temFabricante = Boolean((fabricanteSel?.ref || "").trim());
    const temCompras = Array.isArray(resumoMensalCompras) && resumoMensalCompras.length > 0;

    const totaisComprasAno = {};

    if (isAdminLike && apenasUmAno && temFabricante && temCompras && metric !== "clientes") {
      const ano = anos[0];

      // inicializa array compras
      const comprasPorMes = new Array(12).fill(null);
      for (let m = mi; m <= mf; m++) comprasPorMes[m - 1] = 0;

      totaisComprasAno[ano] = 0;

      for (const r of resumoMensalCompras) {
        const a = Number(r.ano);
        const mes = Number(r.mes);
        if (a !== ano) continue;
        if (mes < mi || mes > mf) continue;

        let valor = 0;
        if (metric === "eur") valor = Number(r.valortotal ?? r.valor ?? 0);
        else if (metric === "litros") valor = Number(r.volume ?? 0);
        else if (metric === "qtd") valor = Number(r.quantidade ?? 0);

        comprasPorMes[mes - 1] += valor;
        totaisComprasAno[ano] += valor;
      }

      // meses futuros do ano atual -> null (igual regra das vendas)
      if (ano === anoAtualLocal && comprasPorMes) {
        for (let m = mesAtualIdx + 1; m < 12; m++) comprasPorMes[m] = null;
      }

      datasets.push({
        label: `${ano}_Compras`,
        data: comprasPorMes,
        tension: 0.4,
        pointRadius: 3,
        borderWidth: 3,
        color: "info",
      });
    }

    return {
      labels,
      datasets,
      totaisAno,
      totaisComprasAno,
    };
  }, [
    isAdminLike,
    resumoMensal,
    resumoMensalCompras,
    anosSel,
    mesIni,
    mesFim,
    metric,
    fabricanteSel,
    modoPeriodo,
    dataInicial,
    dataFinal,
  ]);

  const vendasChartData = useMemo(() => {
    if (!vendasChart) return null;

    return {
      labels: vendasChart.labels,
      datasets: vendasChart.datasets,
    };
  }, [vendasChart]);

  // ============================================================
  // Tabela
  // ============================================================

  const columnsClientes = useMemo(
    () => [
      { Header: "Vendedor", accessor: "vendedor" },
      {
        Header: "Cliente",
        accessor: "cliente",
        Cell: ({ row }) => {
          const nome = row.original.cliente;
          const nif = row.original.clientenif;

          if (nif && String(nif).trim() !== "") {
            return `${nome} (${nif})`;
          }

          return nome;
        },
      },

      { Header: "Ref Cliente", accessor: "refcliente" },
      {
        Header: "€",
        accessor: "valortotal",
        Cell: ({ value }) =>
          value == null
            ? ""
            : Number(value).toLocaleString("pt-PT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      {
        Header: "Litros",
        accessor: "volume",
        Cell: ({ value }) =>
          value == null
            ? ""
            : Number(value).toLocaleString("pt-PT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      {
        Header: "Qtd",
        accessor: "quantidade",
        Cell: ({ value }) => (value == null ? "" : Number(value).toLocaleString("pt-PT")),
      },
    ],
    []
  );

  // ============================================================
  // Helpers de seleção (checkboxes)
  // ============================================================
  const allAnosSelected = anosSel.length === anosOptions.length;
  const toggleAllAnos = () => {
    if (allAnosSelected) setAnosSel([]);
    else setAnosSel([...anosOptions]);
  };
  const toggleAno = (ano) => {
    setAnosSel((prev) => (prev.includes(ano) ? prev.filter((a) => a !== ano) : [...prev, ano]));
  };

  const allVendRefs = vendedores.map((v) => v.ref);
  const allVendedoresSelected =
    allVendRefs.length > 0 && vendedoresSel.length === allVendRefs.length;

  const toggleAllVendedores = () => {
    if (vendedorForcado) return;

    setVendedoresSel(() => (allVendedoresSelected ? [] : allVendRefs));
  };

  const toggleVendedor = (ref) => {
    if (vendedorForcado) return;

    setVendedoresSel((prev) =>
      prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
    );
  };

  const allFamRefs = familias.map((f) => f.ref);
  const allFamiliasSelected = allFamRefs.length > 0 && familiasSel.length === allFamRefs.length;

  const toggleAllFamilias = () => {
    setFamiliasSel(() => (allFamiliasSelected ? [] : allFamRefs));
  };

  const toggleFamilia = (ref) => {
    setFamiliasSel((prev) => (prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]));
  };

  // Só habilita o accordion de Materiais quando Famílias estiverem parcialmente selecionadas:
  // - NÃO pode ser 0 (nenhuma)
  // - NÃO podem ser todas
  const familiasParciais =
    familias.length > 0 && familiasSel.length > 0 && familiasSel.length < familias.length;

  useEffect(() => {
    // Se famílias NÃO estão parciais, desativa Materiais e limpa seleção/lista
    if (!familiasParciais) {
      setMateriaisDisabled(true);
      if (expandedPanel === "materiais") setExpandedPanel(null);
      setMateriais([]);
      setMateriaisSel([]);
      setMateriaisLoadedKey("");
      setMateriaisLoading(false);
      return;
    }

    // Se famílias estão parciais, habilita Materiais
    setMateriaisDisabled(false);

    // Key para evitar recarregar quando a seleção é igual (mesmo conjunto)
    const key = `${fabricanteSel?.ref || ""}::${[...familiasSel].sort().join("|")}`;

    if (!key) return;

    // Se já carregámos para esta key, não faz novo fetch
    if (materiaisLoadedKey === key) return;

    const carregarMateriais = async () => {
      try {
        setMateriaisLoading(true);

        const [dados, err] = await handleApi(
          api.post("Comercial/SelMateriaisCombo", {
            familias: familiasSel,
            refFabricante: fabricanteSel?.ref || "",
          })
        );

        if (err) {
          showAlert(err, "error");
          setMateriais([]);
          setMateriaisSel([]);
          setMateriaisLoadedKey("");
          return;
        }

        const lista = Array.isArray(dados) ? dados : [];

        setMateriais(lista);
        setMateriaisSel(lista.map((m) => m.ref));
        setMateriaisFiltro("");
        setMateriaisLoadedKey(key);
      } catch (e) {
        showAlert("Erro ao carregar materiais.", "error");
        setMateriais([]);
        setMateriaisSel([]);
        setMateriaisFiltro("");
        setMateriaisLoadedKey("");
      } finally {
        setMateriaisLoading(false);
      }
    };

    carregarMateriais();
  }, [familiasParciais, familiasSel, materiaisLoadedKey, expandedPanel, fabricanteSel]);

  // ----------------- Materiais -----------------
  const materiaisVisiveis = useMemo(() => {
    const f = (materiaisFiltro || "").trim().toLowerCase();
    if (!f) return materiais;

    return materiais.filter((m) => {
      const nome = (m.nome || "").toString().toLowerCase();
      const ref = (m.ref || "").toString().toLowerCase();
      return nome.includes(f) || ref.includes(f);
    });
  }, [materiais, materiaisFiltro]);

  const visiveisMatRefs = materiaisVisiveis.map((m) => m.ref);

  const allMateriaisSelected =
    visiveisMatRefs.length > 0 && visiveisMatRefs.every((r) => materiaisSel.includes(r));

  const toggleAllMateriais = () => {
    setMateriaisSel((prev) => {
      if (visiveisMatRefs.length === 0) return prev;

      // se já estão todos os visíveis selecionados, remove só os visíveis
      if (allMateriaisSelected) {
        return prev.filter((r) => !visiveisMatRefs.includes(r));
      }

      // caso contrário, adiciona os visíveis (sem duplicar)
      const setPrev = new Set(prev);
      visiveisMatRefs.forEach((r) => setPrev.add(r));
      return Array.from(setPrev);
    });
  };

  const toggleMaterial = (ref) => {
    setMateriaisSel((prev) =>
      prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
    );
  };

  // Embalagens
  const allEmbRefs = embalagens.map((e) => e.ref);
  const allEmbalagensSelected = allEmbRefs.length > 0 && embalagensSel.length === allEmbRefs.length;

  const toggleAllEmbalagens = () => {
    setEmbalagensSel(() => (allEmbalagensSelected ? [] : allEmbRefs));
  };

  const toggleEmbalagem = (ref) => {
    setEmbalagensSel((prev) =>
      prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
    );
  };

  // ============================================================
  // Render – filtros topo
  // ============================================================
  const filtrosTopo = (
    <MDBox px={2} py={2} display="flex" flexDirection="column" gap={1.75}>
      <MDBox>
        <MDTypography
          variant="button"
          sx={{
            fontSize: 12,
            mb: 1,
            display: "block",
          }}
        >
          Modo do período
        </MDTypography>

        <ToggleButtonGroup
          value={modoPeriodo}
          exclusive
          size="small"
          onChange={(_, novoModo) => {
            if (!novoModo) return;

            setModoPeriodo(novoModo);
            setExpandedPanel((prev) => (prev === "anos" ? null : prev));
            limparResultadosAnalise();
          }}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              px: 2,
              fontSize: 12,
            },
          }}
        >
          <ToggleButton value="entreDatas">Entre datas</ToggleButton>
          <ToggleButton value="comparativo">Comparativo anual</ToggleButton>
        </ToggleButtonGroup>
      </MDBox>

      {/* Linha datas / fabricante / cliente / manter filtros / botão */}
      <MDBox display="flex" flexWrap="wrap" alignItems="flex-end" columnGap={2} rowGap={1.5}>
        {modoPeriodo === "comparativo" && (
          <>
            {/* Mês inicial */}
            <MDBox sx={{ minWidth: 130 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Mês inicial
              </MDTypography>
              <TextField
                select
                size="small"
                value={mesIni}
                onChange={(e) => setMesIni(Number(e.target.value))}
                fullWidth
                SelectProps={{ native: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              >
                {MESES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </TextField>
            </MDBox>

            {/* Dia inicial */}
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Dia inicial
              </MDTypography>
              <TextField
                select
                size="small"
                value={diaIni}
                onChange={(e) => setDiaIni(Number(e.target.value))}
                fullWidth
                SelectProps={{ native: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              >
                {DIAS.map((d) => (
                  <option key={d} value={d}>
                    {d.toString().padStart(2, "0")}
                  </option>
                ))}
              </TextField>
            </MDBox>

            {/* Mês final */}
            <MDBox sx={{ minWidth: 130 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Mês final
              </MDTypography>
              <TextField
                select
                size="small"
                value={mesFim}
                onChange={(e) => setMesFim(Number(e.target.value))}
                fullWidth
                SelectProps={{ native: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              >
                {MESES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </TextField>
            </MDBox>

            {/* Dia final */}
            <MDBox sx={{ minWidth: 90 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Dia final
              </MDTypography>
              <TextField
                select
                size="small"
                value={diaFim}
                onChange={(e) => setDiaFim(Number(e.target.value))}
                fullWidth
                SelectProps={{ native: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              >
                {DIAS.map((d) => (
                  <option key={d} value={d}>
                    {d.toString().padStart(2, "0")}
                  </option>
                ))}
              </TextField>
            </MDBox>
          </>
        )}

        {modoPeriodo === "entreDatas" && (
          <>
            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data inicial
              </MDTypography>

              <TextField
                type="date"
                size="small"
                value={dataInicial}
                onChange={(e) => {
                  setDataInicial(e.target.value);
                  limparResultadosAnalise();
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data final
              </MDTypography>

              <TextField
                type="date"
                size="small"
                value={dataFinal}
                onChange={(e) => {
                  setDataFinal(e.target.value);
                  limparResultadosAnalise();
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              />
            </MDBox>
          </>
        )}

        {/* Fabricante */}
        <MDBox sx={{ minWidth: 220 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Fabricante
          </MDTypography>
          <Autocomplete
            size="small"
            options={fabricantes}
            value={fabricanteSel}
            onChange={(_, value) => setFabricanteSel(value)}
            getOptionLabel={(opt) =>
              opt
                ? `${opt.nome || opt.descricao || opt.designacao || ""}${
                    opt.ref ? ` (${opt.ref})` : ""
                  }`
                : ""
            }
            isOptionEqualToValue={(opt, val) => String(opt?.ref || "") === String(val?.ref || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="TODOS"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              />
            )}
          />
        </MDBox>

        {/* Cliente */}
        <MDBox sx={{ minWidth: 260 }}>
          <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
            Cliente
          </MDTypography>
          <Autocomplete
            size="small"
            options={clientes}
            value={clienteSel}
            onChange={(_, value) => setClienteSel(value)}
            getOptionLabel={(opt) =>
              opt ? `${opt.nome || opt.designacao || ""}${opt.ref ? ` (${opt.ref})` : ""}` : ""
            }
            isOptionEqualToValue={(opt, val) => String(opt?.ref || "") === String(val?.ref || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="TODOS"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              />
            )}
          />
        </MDBox>
      </MDBox>

      {/* Zona Anos */}
      {modoPeriodo === "comparativo" && (
        <Accordion
          expanded={expandedPanel === "anos"}
          onChange={handleAccordionChange("anos")}
          disableGutters
          sx={{
            backgroundColor: "#fff7f0",
            borderRadius: 2,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 32,
              px: 1.5,
              "& .MuiAccordionSummary-content": { margin: "4px 0" },
            }}
          >
            <MDTypography variant="button" sx={{ fontSize: 12 }}>
              Anos
            </MDTypography>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
            <MDBox mb={0.5}>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={allAnosSelected} onChange={toggleAllAnos} />
                }
                label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
              />
            </MDBox>

            <MDBox display="flex" flexWrap="wrap" gap={1}>
              {anosOptions.map((ano) => (
                <FormControlLabel
                  key={ano}
                  control={
                    <Checkbox
                      size="small"
                      checked={anosSel.includes(ano)}
                      onChange={() => toggleAno(ano)}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>{ano}</span>}
                  sx={{ mr: 2 }}
                />
              ))}
            </MDBox>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Zona Vendedores */}
      {!vendedorForcado && (
        <Accordion
          expanded={expandedPanel === "vendedores"}
          onChange={handleAccordionChange("vendedores")}
          disableGutters
          disabled={!!vendedorForcado} // Comercial: accordion inativo
          sx={{
            backgroundColor: "#fff7f0",
            borderRadius: 2,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 32,
              px: 1.5,
              "& .MuiAccordionSummary-content": { margin: "4px 0" },
            }}
          >
            <MDTypography variant="button" sx={{ fontSize: 12 }}>
              Vendedores
            </MDTypography>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
            <MDBox mb={0.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allVendedoresSelected}
                    onChange={toggleAllVendedores}
                    disabled={!!vendedorForcado}
                  />
                }
                label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
              />
            </MDBox>

            <MDBox display="flex" flexWrap="wrap" gap={1}>
              {vendedores.map((v) => (
                <MDBox key={v.ref} sx={{ minWidth: 220 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={vendedoresSel.includes(v.ref)}
                        onChange={() => toggleVendedor(v.ref)}
                        disabled={!!vendedorForcado}
                      />
                    }
                    label={<span style={{ fontSize: 13 }}>{v.nome || v.designacao || v.ref}</span>}
                  />
                </MDBox>
              ))}
            </MDBox>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Zona Famílias */}
      <Accordion
        expanded={expandedPanel === "familias"}
        onChange={handleAccordionChange("familias")}
        disableGutters
        sx={{
          backgroundColor: "#fff7f0",
          borderRadius: 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 32,
            px: 1.5,
            "& .MuiAccordionSummary-content": { margin: "4px 0" },
          }}
        >
          <MDTypography variant="button" sx={{ fontSize: 12 }}>
            Famílias de materiais
          </MDTypography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
          <MDBox mb={0.5}>
            <FormControlLabel
              control={
                <Checkbox size="small" checked={allFamiliasSelected} onChange={toggleAllFamilias} />
              }
              label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todas</span>}
            />
          </MDBox>

          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {familias.map((f) => (
              <MDBox key={f.ref} sx={{ minWidth: 260 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={familiasSel.includes(f.ref)}
                      onChange={() => toggleFamilia(f.ref)}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>{f.nome || f.designacao || f.ref}</span>}
                />
              </MDBox>
            ))}
          </MDBox>
        </AccordionDetails>
      </Accordion>

      {/* Zona Materiais */}
      <Accordion
        expanded={expandedPanel === "materiais"}
        onChange={handleAccordionChange("materiais")}
        disableGutters
        disabled={materiaisDisabled}
        sx={{
          backgroundColor: "#fff7f0",
          borderRadius: 2,
          "&:before": { display: "none" },
          opacity: materiaisDisabled ? 0.65 : 1,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 32,
            px: 1.5,
            "& .MuiAccordionSummary-content": { margin: "4px 0" },
          }}
        >
          <MDTypography variant="button" sx={{ fontSize: 12 }}>
            Materiais
          </MDTypography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
          {materiaisDisabled ? (
            <MDTypography variant="caption" color="text">
              Para escolher materiais, selecione apenas algumas famílias (não todas / não nenhuma).
            </MDTypography>
          ) : materiaisLoading ? (
            <MDBox py={1} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} />
              <MDTypography variant="caption" color="text">
                A carregar materiais…
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDBox mb={1}>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5 }}>
                  Filtrar materiais
                </MDTypography>

                <TextField
                  value={materiaisFiltro}
                  onChange={(e) => setMateriaisFiltro(e.target.value)}
                  placeholder="Escreva para filtrar por nome ou ref…"
                  size="small"
                  fullWidth
                />
              </MDBox>
              <MDBox mb={0.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allMateriaisSelected}
                      onChange={toggleAllMateriais}
                    />
                  }
                  label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
                />
              </MDBox>

              <MDBox
                sx={{
                  maxHeight: 220, // ~ 4 linhas (ajusta se necessário)
                  overflowY: "auto",
                  pr: 1,
                }}
              >
                <MDBox display="flex" flexWrap="wrap" gap={1}>
                  {materiaisVisiveis.map((m) => (
                    <MDBox key={m.ref} sx={{ minWidth: 260 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={materiaisSel.includes(m.ref)}
                            onChange={() => toggleMaterial(m.ref)}
                          />
                        }
                        label={<span style={{ fontSize: 13 }}>{m.nome || m.ref}</span>}
                      />
                    </MDBox>
                  ))}

                  {materiaisVisiveis.length === 0 && (
                    <MDTypography variant="caption" color="text">
                      Sem resultados para os filtros indicados.
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Zona Embalagens */}
      <Accordion
        expanded={expandedPanel === "embalagens"}
        onChange={handleAccordionChange("embalagens")}
        disableGutters
        sx={{
          backgroundColor: "#fff7f0",
          borderRadius: 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 32,
            px: 1.5,
            "& .MuiAccordionSummary-content": { margin: "4px 0" },
          }}
        >
          <MDTypography variant="button" sx={{ fontSize: 12 }}>
            Embalagens
          </MDTypography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
          <MDBox mb={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={allEmbalagensSelected}
                  onChange={toggleAllEmbalagens}
                />
              }
              label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todas</span>}
            />
          </MDBox>

          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {embalagens.map((e) => (
              <MDBox key={e.ref} sx={{ minWidth: 220 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={embalagensSel.includes(e.ref)}
                      onChange={() => toggleEmbalagem(e.ref)}
                    />
                  }
                  // mostra o NOME; a ref só serve de chave / fallback
                  label={<span style={{ fontSize: 13 }}>{e.nome || e.designacao || e.ref}</span>}
                />
              </MDBox>
            ))}
          </MDBox>
        </AccordionDetails>
      </Accordion>

      {/* Filtro por nome do material + métrica */}
      <MDBox
        mt={1}
        display="flex"
        flexWrap="wrap"
        alignItems="flex-end"
        justifyContent="space-between"
        columnGap={2}
        rowGap={1.5}
      >
        <MDBox display="flex" flexWrap="wrap" alignItems="flex-end" columnGap={2} rowGap={1.5}>
          <MDBox>
            <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
              Nome do material
            </MDTypography>
            <RadioGroup
              row
              value={nomeFiltroModo}
              onChange={(e) => setNomeFiltroModo(e.target.value)}
              sx={{ "& .MuiTypography-root": { fontSize: 12 } }}
            >
              <FormControlLabel value="contém" control={<Radio size="small" />} label="Contém" />
              <FormControlLabel
                value="naoContem"
                control={<Radio size="small" />}
                label="Não contém"
              />
            </RadioGroup>
          </MDBox>

          <MDBox sx={{ minWidth: 260 }}>
            <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
              Texto a procurar
            </MDTypography>
            <TextField
              size="small"
              value={nomeFiltroTexto}
              onChange={(e) => setNomeFiltroTexto(e.target.value)}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: 13,
                },
              }}
            />
          </MDBox>
        </MDBox>

        <MDBox
          display="flex"
          flexWrap="wrap"
          alignItems="flex-end"
          justifyContent="flex-end"
          gap={2}
        >
          <MDBox>
            <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
              Métrica
            </MDTypography>

            <RadioGroup
              row
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              sx={{ "& .MuiTypography-root": { fontSize: 12 } }}
            >
              <FormControlLabel value="eur" control={<Radio size="small" />} label="Euros" />
              <FormControlLabel value="litros" control={<Radio size="small" />} label="Litros" />
              <FormControlLabel value="qtd" control={<Radio size="small" />} label="Quantidades" />
              <FormControlLabel
                value="clientes"
                control={<Radio size="small" />}
                label="Clientes"
              />
            </RadioGroup>
          </MDBox>

          {metric !== "clientes" && (
            <MDBox sx={{ minWidth: 150 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Máx. linhas de detalhe
              </MDTypography>

              <TextField
                select
                size="small"
                value={limiteLinhas}
                onChange={(e) => setLimiteLinhas(Number(e.target.value))}
                fullWidth
                SelectProps={{ native: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                  },
                }}
              >
                {limiteLinhasOpcoes.map((opcao) => (
                  <option key={opcao.value} value={opcao.value} disabled={opcao.disabled}>
                    {opcao.label}
                  </option>
                ))}
              </TextField>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          {/* Filtros */}
          {filtrosTopo}

          {/* Botão de atualização manual */}
          <MDBox
            sx={{
              mb: 2,
              mr: 2,
              ml: "auto",
              minWidth: 160,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
            }}
          >
            {combosDependentesDesatualizadas && (
              <MDTypography variant="caption" color="warning" sx={{ fontSize: 12 }}>
                Combos de vendedores/clientes desatualizadas para o período atual.
              </MDTypography>
            )}

            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              disabled={loading}
              onClick={() => {
                if (!validarPeriodoAtual()) return;

                setMostrarResultados(true);
                setTabResultados(0);

                setMateriaisAgrupados([]);
                setResumoMateriais(null);
                setMateriaisAgrupadosCarregados(false);

                carregarDados();
              }}
            >
              Atualizar análise
            </MDButton>
          </MDBox>

          {mostrarResultados && loading && (
            <MDBox px={2} pb={1} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          )}

          {/* Chart – MESMO MODELO do Dashboard/MenuSearchMateriais */}
          {mostrarResultados && (
            <MDBox px={2} pb={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <MDTypography variant="h6">{METRIC_CONFIG[metric].title}</MDTypography>
              </MDBox>

              <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
                {modoPeriodo === "entreDatas"
                  ? metric === "clientes"
                    ? "Número de clientes diferentes em cada mês do período."
                    : `Total do período indicado na legenda. ${
                        metric === "eur"
                          ? "Valores em euros."
                          : metric === "litros"
                          ? "Litros."
                          : metric === "qtd"
                          ? "Quantidades."
                          : "Peso bruto."
                      }`
                  : METRIC_CONFIG[metric].caption}
              </MDTypography>

              {!vendasChart ? (
                <MDBox py={4} display="flex" justifyContent="center">
                  <MDTypography variant="button" color="text">
                    Sem dados com os filtros selecionados.
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <MDBox mb={1.5}>
                    <MemoizedDefaultLineChart chart={vendasChartData} height="16rem" />
                  </MDBox>

                  {/* Legenda equivalente ao Dashboard */}
                  <MDBox display="flex" flexWrap="wrap" gap={2} mb={0.5}>
                    {vendasChart.datasets.map((ds) => {
                      const label = String(ds.label || "");
                      const ano = label.includes("_") ? label.split("_")[0] : label;
                      const isCompras = label.endsWith("_Compras");
                      const totalAno =
                        modoPeriodo === "entreDatas"
                          ? vendasChart.totaisDataset?.[label] ?? 0
                          : isCompras
                          ? vendasChart.totaisComprasAno?.[ano] ?? 0
                          : vendasChart.totaisAno?.[ano] ?? 0;

                      const suffix = METRIC_CONFIG[metric].suffix;

                      return (
                        <MDTypography
                          key={ds.label}
                          variant="button"
                          color="text"
                          sx={{ fontSize: 12, display: "inline-flex", alignItems: "center" }}
                        >
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              marginRight: 6,
                              backgroundColor: colorMap[ds.color] || "#999",
                              display: "inline-block",
                            }}
                          />
                          {label}
                          <span style={{ marginLeft: 4 }}>
                            (
                            <strong>
                              {fmt.format(totalAno)}
                              {suffix}
                            </strong>
                            )
                          </span>
                        </MDTypography>
                      );
                    })}
                  </MDBox>
                </>
              )}
            </MDBox>
          )}

          {mostrarResultados && (
            <>
              {loading && (
                <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
                  <CircularProgress />
                </MDBox>
              )}

              {metric !== "clientes" && (
                <MDBox px={2} pb={2}>
                  <MDBox
                    sx={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: 2,
                      mb: 2,
                      px: 1,
                    }}
                  >
                    <Tabs
                      value={tabResultados}
                      onChange={(_, novoValor) => {
                        setTabResultados(novoValor);

                        if (novoValor === 1 && !materiaisAgrupadosCarregados) {
                          carregarMateriaisAgrupados();
                        }
                      }}
                      aria-label="Resultados da análise de vendas"
                      variant="fullWidth"
                      textColor="inherit"
                      TabIndicatorProps={{
                        style: {
                          backgroundColor: "#d32f2f",
                          height: 3,
                        },
                      }}
                      sx={{
                        minHeight: 46,

                        "& .MuiTabs-flexContainer": {
                          width: "100%",
                        },

                        "& .MuiTab-root": {
                          fontSize: 13,
                          textTransform: "none",
                          minHeight: 46,
                          color: "#344767",
                        },

                        "& .Mui-selected": {
                          color: "#d32f2f !important",
                          fontWeight: 600,
                        },
                      }}
                    >
                      <Tab label="Detalhe das vendas" />
                      <Tab label="Vendas por material" />
                    </Tabs>
                  </MDBox>

                  {tabResultados === 0 && (
                    <AnaliseVendasDetalheTable
                      linhas={rows}
                      totalLinhas={totalLinhas}
                      metric={metric}
                      imgsByRef={imgsByRef}
                      loading={false}
                    />
                  )}

                  {tabResultados === 1 && (
                    <AnaliseVendasMateriaisTable
                      materiais={materiaisAgrupados}
                      loading={materiaisAgrupadosLoading}
                      imgsByRef={imgsByRef}
                      onAbrirDetalhe={abrirDetalheMaterial}
                    />
                  )}
                </MDBox>
              )}

              {metric === "clientes" && (
                <MDBox px={2} pb={2}>
                  <ProjectTable
                    tableKey="analise-vendas-clientes"
                    version="v1"
                    title={`Clientes por vendedor (${clientesPorVendedor.length} distintos)`}
                    columns={columnsClientes}
                    data={clientesPorVendedor}
                    loading={false}
                    emptyMessage="Sem clientes a apresentar."
                    minWidth={950}
                    maxHeight="50vh"
                    defaultSortBy={[
                      {
                        id: "vendedor",
                        desc: false,
                      },
                    ]}
                    showKeepFilters
                  />
                </MDBox>
              )}
            </>
          )}
        </Card>
      </MDBox>

      <AnaliseVendasMaterialDetalheDialog
        open={dialogMaterialOpen}
        onClose={fecharDetalheMaterial}
        material={materialDetalheSelecionado}
        linhas={linhasDetalheMaterial}
        loading={detalheMaterialLoading}
        imgsByRef={imgsByRef}
      />
    </DashboardLayout>
  );
}

export default AnaliseVendas;
